import type { RippleAdapter } from '../types';

/**
 * Ripple adapter for React state updates
 * This adapter allows the tutorial system to trigger React state changes
 * through callbacks rather than direct DOM manipulation
 */
export class BrowserRippleAdapter implements RippleAdapter {
  private modalCallbacks: Map<string, () => void> = new Map();
  private formUpdateCallbacks: Map<string, (value: unknown) => void> = new Map();
  private formSubmitCallbacks: Map<string, () => void> = new Map();
  private formValueGetters: Map<string, () => unknown> = new Map();

  /**
   * Register a callback to open a modal
   */
  registerModalOpen(modalId: string, callback: () => void): void {
    this.modalCallbacks.set(`open:${modalId}`, callback);
  }

  /**
   * Register a callback to close a modal
   */
  registerModalClose(modalId: string, callback: () => void): void {
    this.modalCallbacks.set(`close:${modalId}`, callback);
  }

  /**
   * Register a callback to update a form field
   */
  registerFormFieldUpdate(fieldName: string, callback: (value: unknown) => void): void {
    this.formUpdateCallbacks.set(fieldName, callback);
  }

  /**
   * Register a callback to submit a form
   */
  registerFormSubmit(formId: string, callback: () => void): void {
    this.formSubmitCallbacks.set(formId, callback);
  }

  /**
   * Register a getter for form field value
   */
  registerFormValueGetter(fieldName: string, getter: () => unknown): void {
    this.formValueGetters.set(fieldName, getter);
  }

  // Public API methods

  openModal(modalId: string): void {
    const callback = this.modalCallbacks.get(`open:${modalId}`);
    if (callback) {
      callback();
    } else {
      console.warn(`No callback registered for opening modal: ${modalId}`);
    }
  }

  closeModal(modalId: string): void {
    const callback = this.modalCallbacks.get(`close:${modalId}`);
    if (callback) {
      callback();
    } else {
      console.warn(`No callback registered for closing modal: ${modalId}`);
    }
  }

  updateFormField(fieldName: string, value: unknown): void {
    const callback = this.formUpdateCallbacks.get(fieldName);
    if (callback) {
      callback(value);
    } else {
      console.warn(`No callback registered for form field: ${fieldName}`);
    }
  }

  submitForm(formId: string): void {
    const callback = this.formSubmitCallbacks.get(formId);
    if (callback) {
      callback();
    } else {
      console.warn(`No callback registered for form submit: ${formId}`);
    }
  }

  getFormValue(fieldName: string): unknown {
    const getter = this.formValueGetters.get(fieldName);
    if (getter) {
      return getter();
    }
    console.warn(`No getter registered for form field: ${fieldName}`);
    return undefined;
  }
}

/**
 * Mock ripple adapter for testing
 */
export class MockRippleAdapter implements RippleAdapter {
  private modalsOpened: string[] = [];
  private modalsClosed: string[] = [];
  private formUpdates: Map<string, unknown[]> = new Map();
  private formsSubmitted: string[] = [];

  openModal(modalId: string): void {
    this.modalsOpened.push(modalId);
  }

  closeModal(modalId: string): void {
    this.modalsClosed.push(modalId);
  }

  updateFormField(fieldName: string, value: unknown): void {
    if (!this.formUpdates.has(fieldName)) {
      this.formUpdates.set(fieldName, []);
    }
    const updates = this.formUpdates.get(fieldName);
    if (updates) {
      updates.push(value);
    }
  }

  submitForm(formId: string): void {
    this.formsSubmitted.push(formId);
  }

  getFormValue(fieldName: string): unknown {
    const updates = this.formUpdates.get(fieldName);
    return updates ? updates[updates.length - 1] : undefined;
  }

  // Test helpers

  wasModalOpened(modalId: string): boolean {
    return this.modalsOpened.includes(modalId);
  }

  wasModalClosed(modalId: string): boolean {
    return this.modalsClosed.includes(modalId);
  }

  getFormUpdateCount(fieldName: string): number {
    return this.formUpdates.get(fieldName)?.length ?? 0;
  }

  wasFormSubmitted(formId: string): boolean {
    return this.formsSubmitted.includes(formId);
  }

  reset(): void {
    this.modalsOpened = [];
    this.modalsClosed = [];
    this.formUpdates.clear();
    this.formsSubmitted = [];
  }
}
